// ** React Imports
import { useState, useEffect } from 'react'

// ** Next Imports

// ** MUI Imports
import Box from '@mui/material/Box'
import Grid from '@mui/material/Grid'
import Card from '@mui/material/Card'
import { styled } from '@mui/material/styles'
import MenuItem from '@mui/material/MenuItem'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'

// ** Custom Components Imports
import {
  recruitmentConfig,
  recruitmentTemplates,
  defaultTierDiff,
  defaultTierDiffGems,
  troopsConf
} from 'src/data/speeds/recruitment'

// ** Styled Components
import CustomHeader from 'src/@core/components/Header'
import {
  Alert,
  AlertTitle,
  Avatar,
  Button,
  Divider,
  FormControlLabel,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Paper,
  Snackbar,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Tooltip
} from '@mui/material'
import { tableCellClasses } from '@mui/material/TableCell'
import { Refresh , Link } from '@mui/icons-material'
import { formatCompactNumber } from 'src/@core/utils/numberFormatter'
import { Icon } from '@iconify/react'
import TablaReferencia from 'src/@core/components/table/TablaReferencia'
import { construirEnlace, leerEstadoDeUrl } from 'src/@core/utils/enlace-compartible'

/*
  Estado compartible. De speedInfo viajan solo las entradas del jugador. Quedan
  fuera timeBatch, totalTroops, totalTroopsGems, totalTroopsGrl y rss, que son
  resultados que la pagina recalcula: meterlos seria enviar el resultado en vez de
  los datos, y un enlace viejo mostraria cifras que ya no salen de ese calculo.
*/
const aEstadoMinimo = info => ({
  g: info?.userInfo?.gems ?? 0,
  ns: info?.userInfo?.ns ?? { d: '', h: '' },
  rs: info?.userInfo?.rs ?? { d: '', h: '' },
  p: info?.profile ?? '',
  d: info?.duke ? 1 : 0,
  b: info?.baron ? 1 : 0,
  r: info?.recruitmentSpeed ?? 0,
  n: info?.nationalRecruitment ? 1 : 0,
  tb: info?.troopsBatch ?? 2000,
  tt: info?.troopTier ?? 13
})

const desdeEstadoMinimo = minimo => {
  if (!minimo || typeof minimo !== 'object') return null
  const texto = v => (v && typeof v === 'object' ? { d: String(v.d ?? ''), h: String(v.h ?? '') } : { d: '', h: '' })

  return {
    ...defaultSpeedInfo,
    userInfo: { gems: Number(minimo.g) || 0, ns: texto(minimo.ns), rs: texto(minimo.rs) },
    profile: typeof minimo.p === 'string' ? minimo.p : '',
    duke: minimo.d === 1,
    baron: minimo.b === 1,
    recruitmentSpeed: Number(minimo.r) || 0,
    nationalRecruitment: minimo.n === 1,
    troopsBatch: Number(minimo.tb) || 2000,
    troopTier: Number(minimo.tt) || 13
  }
}

/*
  Tabla de referencia renderizada en el servidor. /speeds/ servia 482 palabras y
  cero filas: los tiempos por tier viven dentro de un objeto que solo se lee al
  elegir un perfil de velocidad.

  Una fila por perfil y tier. El tiempo esta en el dato en horas con decimales; se
  publica tal cual y ademas formateado, para que sirva igual de consulta rapida y
  de cifra con la que hacer cuentas.
*/
const COLUMNAS_SPEEDS = [
  { clave: 'velocidad', encabezado: 'Recruitment speed (%)', numerica: true },
  { clave: 'lote', encabezado: 'Troops per batch', numerica: true, formato: v => v.toLocaleString('en-US') },
  { clave: 'tier', encabezado: 'Troop tier', numerica: true },
  { clave: 'horas', encabezado: 'Hours per batch', numerica: true, formato: v => v.toFixed(2) },
  { clave: 'legible', encabezado: 'Time' }
]

const enHorasMinutos = h => {
  const total = Math.round(h * 60)

  return Math.floor(total / 60) + 'h ' + String(total % 60).padStart(2, '0') + 'm'
}

const FILAS_SPEEDS = recruitmentTemplates.flatMap((t, i) =>
  Object.entries(t.tierTime ?? {}).map(([tier, horas]) => ({
    id: i + '-' + tier,
    velocidad: t.recruitmentSpeed,
    lote: t.troopsBatch,
    tier: Number(tier),
    horas,
    legible: enHorasMinutos(horas)
  }))
)


const CustomBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  padding: '0.4em'
}))

const GreenButton = styled(Button)(({ theme }) => ({
  // Verde oscuro y texto blanco: #E8F1F5 sobre #1B8A6B daba 3,74:1, por debajo de AA.
  backgroundColor: theme.palette.customColors.skyPaletteTertiaryDark,
  color: theme.palette.common.white,
  '&:hover': {
    backgroundColor: theme.palette.customColors.skyPaletteTertiaryDark,
    filter: 'brightness(0.85)'
  }
}))

const defaultSpeedInfo = {
  userInfo: {
    gems: 0,
    ns: { d: '', h: '' },
    rs: { d: '', h: '' }
  },
  profile: '',
  duke: false,
  baron: false,
  recruitmentSpeed: 0,
  nationalRecruitment: false,
  troopsBatch: 2000,
  troopTier: 13,
  timeBatch: '',
  totalTroops: 0,
  totalTroopsGems: 0,
  totalTroopsGrl: 0,
  rss: {}
}

function toHoursMinutes(totalSeconds) {
  const totalSecs = Math.floor(totalSeconds)
  const totalMinutes = Math.floor(totalSecs / 60)

  const seconds = totalSeconds % 60
  const hours = Math.floor(totalMinutes / 60)
  const minutes = totalMinutes % 60

  return { h: hours, m: minutes, s: seconds }
}

const Speeds = props => {
  const [isLoading, setIsLoading] = useState(true)
  const [speedInfo, setSpeedInfo] = useState(defaultSpeedInfo)
  const [snackOpen, setSnackOpen] = useState({ open: false, type: 'error', time: 0 })

  // ** Hooks
  useEffect(() => {
    // El enlace compartido gana sobre lo guardado; lo propio sigue en localStorage
    const compartido = desdeEstadoMinimo(leerEstadoDeUrl())
    if (compartido) {
      setSpeedInfo(compartido)
      setIsLoading(false)

      return
    }

    let saved
    if (typeof window !== 'undefined') {
      // Perform localStorage action
      saved = JSON.parse(localStorage.getItem('speedInfo'))
    }
    if (saved) {
      setSpeedInfo(saved)
    } else {
      setSpeedInfo(defaultSpeedInfo)
    }
    setIsLoading(false)
  }, [])

  const handleShare = async () => {
    const enlace = construirEnlace(aEstadoMinimo(speedInfo))
    if (!enlace) return setSnackOpen({ open: 'Could not build the link.', type: 'error', time: 4000 })

    try {
      await navigator.clipboard.writeText(enlace)

      return setSnackOpen({ open: 'Link copied — paste it anywhere.', type: 'success', time: 4000 })
    } catch (e) {
      window.history.replaceState(null, '', enlace)

      return setSnackOpen({ open: 'Link is now in the address bar — copy it from there.', type: 'info', time: 6000 })
    }
  }

  const handleSaveData = e => {
    localStorage.setItem('speedInfo', JSON.stringify(speedInfo))
    setSnackOpen({ open: `Information Saved in the Browser.`, type: 'success' })
  }

  const handleRemoveItems = e => {
    setSpeedInfo(defaultSpeedInfo)
    localStorage.removeItem('speedInfo')
    setSnackOpen({ open: `Information Cleared from the Browser`, type: 'error' })
  }

  const snackClose = (event, reason) => {
    if (reason === 'clickaway') {
      return
    }
    setSnackOpen({ open: false, type: 'error', time: 1000 })
  }

  const handleTroopHours = newSpeedInfo => {
    let troopHours
    let recruitmentTime

    if (speedInfo.troopTier.length === 0 || speedInfo.troopsBatch === 0) {
      setSpeedInfo({ ...newSpeedInfo })

      return
    }

    const troopTier = newSpeedInfo.troopTier

    if (troopTier === 14) {
      recruitmentTime =
        recruitmentTemplates?.find(p => p.recruitmentSpeed === newSpeedInfo.profile)?.tierTime[13] *
        defaultTierDiff[troopTier]
    } else {
      recruitmentTime = recruitmentTemplates?.find(p => p.recruitmentSpeed === newSpeedInfo.profile)?.tierTime[
        troopTier
      ]
    }

    const extraBuffs = newSpeedInfo.baron
      ? recruitmentConfig.default.baronPercent
      : newSpeedInfo.duke
        ? recruitmentConfig.default.dukePercent
        : false

    const templeBuffs =
      newSpeedInfo.nationalRecruitment?.calcPercent > 0 ? newSpeedInfo.nationalRecruitment?.calcPercent : 0

    troopHours = recruitmentTime * (extraBuffs > 0 ? extraBuffs : 1) * (templeBuffs > 0 ? templeBuffs : 1)
    troopHours = troopHours * newSpeedInfo.troopsBatch

    const timeArray = toHoursMinutes(troopHours)
    setSpeedInfo({ ...newSpeedInfo, timeBatch: `${timeArray.h}h${timeArray.m}m${Math.floor(timeArray.s)}s` })
  }

  const handleCalculate = e => {
    let totalTroopsGems, totalTroops, calcPercent, totalTroopsGrl, newRssObj

    // Speeds and troops data
    const { troopTier, profile, userInfo, baron, duke, nationalRecruitment } = speedInfo
    const ns = userInfo.ns.d * 24 + userInfo.ns.h
    const rs = userInfo.rs.d * 24 + userInfo.rs.h
    const totalHours = (+ns + +rs) * 60 * 60

    // Buffs data
    const extraBuffs = baron
      ? recruitmentConfig.default.baronPercent
      : duke
        ? recruitmentConfig.default.dukePercent
        : false

    const templeBuffs = nationalRecruitment?.calcPercent > 0 ? nationalRecruitment?.calcPercent : 0

    // Troops Speed Calculation
    if (troopTier === 14) {
      calcPercent =
        recruitmentTemplates?.find(p => p.recruitmentSpeed === profile)?.tierTime[13] * defaultTierDiff[troopTier]
      totalTroops = totalHours / (calcPercent * (extraBuffs > 0 ? extraBuffs : 1) * (templeBuffs > 0 ? templeBuffs : 1))
    } else {
      totalTroops =
        totalHours /
        (recruitmentTemplates?.find(p => p.recruitmentSpeed === profile)?.tierTime[troopTier] *
          (extraBuffs > 0 ? extraBuffs : 1) *
          (templeBuffs > 0 ? templeBuffs : 1))
    }

    // Gems Speeds Calculation
    if (userInfo.gems > 0) {
      const profileObj = recruitmentTemplates?.find(p => p.recruitmentSpeed === profile)?.gemQty ?? undefined
      if (profileObj) {
        if (troopTier === 14) {
          const gemsDiff =
            recruitmentTemplates?.find(p => p.recruitmentSpeed === profile)?.gemQty[13] * defaultTierDiffGems[troopTier]
          totalTroopsGems =
            userInfo.gems / (gemsDiff * (extraBuffs > 0 ? extraBuffs : 1) * (templeBuffs > 0 ? templeBuffs : 1))
        } else {
          totalTroopsGems =
            userInfo.gems /
            (recruitmentTemplates?.find(p => p.recruitmentSpeed === profile)?.gemQty[troopTier] *
              (extraBuffs > 0 ? extraBuffs : 1) *
              (templeBuffs > 0 ? templeBuffs : 1))
        }
      }
    }

    if (totalTroops > 0) totalTroopsGrl = totalTroops
    if (totalTroopsGems > 0) totalTroopsGrl = totalTroops + totalTroopsGems

    if (totalTroops > 0) {
      const infantry = troopsConf?.troopsRecruitmentRSS?.infantry[troopTier]
      const cavalry = troopsConf?.troopsRecruitmentRSS?.cavalry[troopTier]
      const archer = troopsConf?.troopsRecruitmentRSS?.archer[troopTier]
      const mage = troopsConf?.troopsRecruitmentRSS?.mage[troopTier]

      newRssObj = !infantry
        ? []
        : [
            {
              name: 'Infantry',
              key: 'infantry',
              f: infantry?.f * totalTroopsGrl,
              w: infantry?.w * totalTroopsGrl,
              s: infantry?.s * totalTroopsGrl,
              i: infantry?.i * totalTroopsGrl
            },
            {
              name: 'Cavalry',
              key: 'cavalry',
              f: cavalry?.f * totalTroopsGrl,
              w: cavalry?.w * totalTroopsGrl,
              s: cavalry?.s * totalTroopsGrl,
              i: cavalry?.i * totalTroopsGrl
            },
            {
              name: 'Archer',
              key: 'archer',
              f: archer?.f * totalTroopsGrl,
              w: archer?.w * totalTroopsGrl,
              s: archer?.s * totalTroopsGrl,
              i: archer?.i * totalTroopsGrl
            },
            {
              name: 'Mage',
              key: 'mage',
              f: mage?.f * totalTroopsGrl,
              w: mage?.w * totalTroopsGrl,
              s: mage?.s * totalTroopsGrl,
              i: mage?.i * totalTroopsGrl
            }
          ]
    }

    setSpeedInfo({
      ...speedInfo,
      totalTroops: totalTroops,
      totalTroopsGems: totalTroopsGems,
      totalTroopsGrl: totalTroopsGrl,
      rss: newRssObj
    })
  }

  return (
    <>
      <Grid container spacing={6}>
        <Grid size={{ xs: 12 }}>
          <Card
            sx={{
              padding: '2rem',
              borderRadius: '12px',
              boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.1)',
              borderTop: '6px solid',
              borderTopColor: 'primary.main'
            }}
          >
            <CustomHeader icon='speeds' title='SPEEDS CALCULATOR' />
            <Divider sx={{ marginY: '1rem' }} />
            <Typography variant='h6' component='h2' sx={{ fontWeight: 'bold', marginBottom: '1rem', color: 'primary.main' }}>
              Recruitment Speed Calculator
            </Typography>
            <Typography variant='body2' sx={{ color: 'text.secondary', mb: 1 }}>
              Troop training speed in War and Order depends on a combination of research, hero skills, alliance bonuses,
              and temporary speed-up items. This calculator lets you input your total training speed buff percentage and
              troop count to estimate exactly how long recruitment will take.
            </Typography>
            <Typography variant='body2' sx={{ color: 'text.secondary', mb: 1 }}>
              Configure your queue size, hospital capacity, and buff values to get a realistic projection. Use this
              before a rally or Anubis event to make sure you can fully replenish troops in time.
            </Typography>
            <Typography variant='body2' sx={{ color: 'text.secondary' }}>
              Your configuration is saved locally. Adjust values and the result updates instantly.
            </Typography>
          </Card>
        </Grid>
      </Grid>
      <>
        <Card>
          <Snackbar
            open={snackOpen.open ? true : false}
            autoHideDuration={snackOpen.time ?? 7000}
            onClose={snackClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          >
            <Alert variant='filled' onClose={snackClose} severity={snackOpen.type}>
              <AlertTitle>{snackOpen.type === 'success' ? 'Done!' : 'Info:'}</AlertTitle>
              {snackOpen.open}
            </Alert>
          </Snackbar>
          <Grid container spacing={3} style={{ minWidth: '320px', padding: '1rem' }}>
            <Grid size={{ xs: 12 }}>
              <Paper elevation={0} sx={{ padding: '1rem' }}>
                <Divider>{<Typography variant='caption'>1 - USER CONFIGURATION</Typography>}</Divider>
                <CustomBox>
                  <Typography variant='body1' color='textPrimary' sx={{ marginRight: '5px' }}>
                    Gems:
                  </Typography>
                  <TextField
                    slotProps={{ htmlInput: { 'aria-label': 'Gems' } }}
                    size='small'
                    id='gems-u'
                    variant='outlined'
                    type='number'
                    value={speedInfo.userInfo.gems > 0 ? speedInfo.userInfo.gems : ''}
                    sx={{ maxWidth: '8.2rem' }}
                    onChange={e => {
                      const maxNumber = 9999999
                      const value = +e.target.value * 1 >= maxNumber ? maxNumber : +e.target.value * 1
                      const newUserInfo = { ...speedInfo.userInfo, gems: value }
                      setSpeedInfo({ ...speedInfo, userInfo: newUserInfo })
                    }}
                  />
                </CustomBox>
                <CustomBox>
                  <Typography variant='body1' color='textPrimary' sx={{ marginRight: '5px' }}>
                    Normal Speeds:
                  </Typography>
                  <TextField
                    label='Days'
                    size='small'
                    id='nspeeds-ud'
                    variant='outlined'
                    type='number'
                    value={speedInfo.userInfo.ns.d > 0 ? speedInfo.userInfo.ns.d : ''}
                    sx={{ maxWidth: '5.2rem', marginRight: '3px' }}
                    onChange={e => {
                      const maxNumber = 9999
                      let value = +e.target.value * 1 >= maxNumber ? maxNumber : +e.target.value * 1
                      const newUserInfo = { ...speedInfo.userInfo, ns: { ...speedInfo.userInfo.ns, d: value } }
                      setSpeedInfo({ ...speedInfo, userInfo: newUserInfo })
                    }}
                  />
                  <TextField
                    label='Hours'
                    size='small'
                    id='nspeeds-uh'
                    variant='outlined'
                    type='number'
                    value={speedInfo.userInfo.ns.h > 0 ? speedInfo.userInfo.ns.h : ''}
                    sx={{ maxWidth: '5.2rem', marginRight: '5px' }}
                    onChange={e => {
                      const maxNumber = 9999
                      let value = +e.target.value * 1 >= maxNumber ? maxNumber : +e.target.value * 1
                      const newUserInfo = { ...speedInfo.userInfo, ns: { ...speedInfo.userInfo.ns, h: value } }
                      setSpeedInfo({ ...speedInfo, userInfo: newUserInfo })
                    }}
                  />
                  <Typography variant='body2' color='primary'>
                    {`${Math.round(speedInfo.userInfo.ns.d * 24) + speedInfo.userInfo.ns.h} hours.`}
                  </Typography>
                </CustomBox>
                <CustomBox>
                  <Typography variant='body1' color='textPrimary' sx={{ marginRight: '5px' }}>
                    Recruitment Speeds:
                  </Typography>
                  <TextField
                    label='Days'
                    size='small'
                    id='rspeeds-ud'
                    variant='outlined'
                    type='number'
                    value={speedInfo.userInfo.rs.d > 0 ? speedInfo.userInfo.rs.d : ''}
                    sx={{ maxWidth: '5.2rem', marginRight: '3px' }}
                    onChange={e => {
                      const maxNumber = 9999
                      let value = +e.target.value * 1 >= maxNumber ? maxNumber : +e.target.value * 1
                      const newUserInfo = { ...speedInfo.userInfo, rs: { ...speedInfo.userInfo.rs, d: value } }
                      setSpeedInfo({ ...speedInfo, userInfo: newUserInfo })
                    }}
                  />
                  <TextField
                    label='Hours'
                    size='small'
                    id='rspeeds-uh'
                    variant='outlined'
                    type='number'
                    value={speedInfo.userInfo.rs.h > 0 ? speedInfo.userInfo.rs.h : ''}
                    sx={{ maxWidth: '5.2rem', marginRight: '5px' }}
                    onChange={e => {
                      const maxNumber = 9999
                      let value = +e.target.value * 1 >= maxNumber ? maxNumber : +e.target.value * 1
                      const newUserInfo = { ...speedInfo.userInfo, rs: { ...speedInfo.userInfo.rs, h: value } }
                      setSpeedInfo({ ...speedInfo, userInfo: newUserInfo })
                    }}
                  />
                  <Typography variant='body2' color='primary'>
                    {`${Math.round(speedInfo.userInfo.rs.d * 24) + speedInfo.userInfo.rs.h} hours.`}
                  </Typography>
                </CustomBox>
                <Divider>{<Typography variant='caption'>2 - PRESETS</Typography>}</Divider>
                <CustomBox>
                  <TextField
                    select
                    size='small'
                    label='Select Preset'
                    id='load-preset'
                    variant='outlined'
                    type='text'
                    value={speedInfo.profile > 0 ? speedInfo.profile : ''}
                    onChange={e => {
                      handleTroopHours({ ...speedInfo, profile: e.target.value })
                    }}
                    sx={{ marginRight: '5px' }}
                  >
                    {recruitmentTemplates?.map(option => (
                      <MenuItem key={option.recruitmentSpeed} value={option.recruitmentSpeed}>
                        {option.recruitmentSpeed}% {option?.gemQty ? `+ Gems` : ``}
                      </MenuItem>
                    ))}
                  </TextField>
                </CustomBox>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'flex-start',
                    gap: '1rem',
                    padding: '0.4em',
                    alignItems: 'center'
                  }}
                >
                  <GreenButton
                    variant='contained'
                    startIcon={<Icon icon='ic:baseline-save' />}
                    onClick={handleSaveData}
                  >
                    SAVE
                  </GreenButton>
                  <Button variant='outlined' startIcon={<Link />} onClick={handleShare}>
                    SHARE
                  </Button>
                  <Button
                    variant='contained'
                    color='error'
                    startIcon={<Icon icon='ic:baseline-delete' />}
                    onClick={handleRemoveItems}
                  >
                    DELETE
                  </Button>
                </Box>
                {speedInfo.profile > 0 && (
                  <>
                    <Divider>{<Typography variant='caption'>3 - BUFFS</Typography>}</Divider>
                    <CustomBox>
                      <TextField
                        size='small'
                        disabled
                        label='Recruitment %'
                        id='recruitmen-speed'
                        value={
                          speedInfo.profile > 0
                            ? (speedInfo.profile +
                                (speedInfo.duke ? 6 : 0) +
                                (speedInfo.baron ? 5 : 0) +
                                (speedInfo.nationalRecruitment?.percentage > 0
                                  ? speedInfo.nationalRecruitment?.percentage
                                  : 0)) *
                              1
                            : 0
                        }
                        variant='outlined'
                        type='number'
                      />
                    </CustomBox>
                    <CustomBox>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={speedInfo.duke}
                            disabled={speedInfo.profile ? false : true}
                            onChange={e => {
                              handleTroopHours({
                                ...speedInfo,
                                baron: e.target.checked ? false : speedInfo.baron,
                                duke: e.target.checked
                              })
                            }}
                            name='duke-checked'
                            color='primary'
                          />
                        }
                        label='Grand Duke Buff (6%)'
                      />
                    </CustomBox>
                    <CustomBox>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={speedInfo.baron}
                            disabled={speedInfo.profile ? false : true}
                            onChange={e => {
                              handleTroopHours({
                                ...speedInfo,
                                duke: e.target.checked ? false : speedInfo.duke,
                                baron: e.target.checked
                              })
                            }}
                            name='baron-checked'
                            color='primary'
                          />
                        }
                        label='Baron Buff (5%)'
                      />
                    </CustomBox>
                    <CustomBox>
                      <TextField
                        select
                        size='small'
                        label='National Rec. Skill'
                        id='nationalRecruitment'
                        value={speedInfo.nationalRecruitment?.level > 0 ? speedInfo.nationalRecruitment?.level : ''}
                        variant='outlined'
                        disabled={speedInfo.profile ? false : true}
                        onChange={e => {
                          const nrObj = recruitmentConfig?.default?.nationalRecruitmentList?.find(
                            nr => nr.level === e.target.value
                          )
                          handleTroopHours({ ...speedInfo, nationalRecruitment: nrObj })
                        }}
                      >
                        {recruitmentConfig?.default?.nationalRecruitmentList?.map(option => (
                          <MenuItem key={option.level} value={option.level}>
                            {option.percentage}% (Lvl {option.level})
                          </MenuItem>
                        ))}
                      </TextField>
                    </CustomBox>

                    <Divider>{<Typography variant='caption'>4 - TROOPS</Typography>}</Divider>
                    <CustomBox>
                      <TextField
                        label='Troops Per Batch'
                        id='troops-batch'
                        size='small'
                        variant='outlined'
                        type='number'
                        disabled={speedInfo.profile > 0 ? false : true}
                        value={speedInfo.troopsBatch > 0 ? speedInfo.troopsBatch : ''}
                        onChange={e => {
                          const value = +e.target.value > 4500 ? 4500 : +e.target.value <= 1 ? 1 : +e.target.value
                          handleTroopHours({ ...speedInfo, troopsBatch: value })
                        }}
                        sx={{ maxWidth: '170px', marginRight: '5px' }}
                      />
                      <TextField
                        select
                        label='Troops Tier'
                        id='troops-points'
                        size='small'
                        variant='outlined'
                        type='number'
                        value={speedInfo.troopTier > 0 ? speedInfo.troopTier : 13}
                        disabled={speedInfo.profile > 0 ? false : true}
                        onChange={e => {
                          handleTroopHours({ ...speedInfo, troopTier: e.target.value })
                        }}
                      >
                        {recruitmentConfig?.default?.troopsPoints?.map(option => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.value}
                          </MenuItem>
                        ))}
                      </TextField>
                    </CustomBox>
                    <CustomBox>
                      <TextField
                        disabled
                        label='Total time per Batch'
                        id='hours-per-batch'
                        value={speedInfo.timeBatch.length > 0 ? speedInfo.timeBatch : ''}
                        size='small'
                        variant='outlined'
                        type='text'
                      />
                    </CustomBox>

                    <Box sx={{ display: 'flex', justifyContent: 'center', m: 3 }}>
                      <Button
                        disabled={
                          speedInfo.troopTier.length === 0 || speedInfo.troopsBatch === 0
                            ? true
                            : speedInfo.userInfo.ns.d * 24 +
                                  speedInfo.userInfo.ns.h +
                                  speedInfo.userInfo.rs.d * 24 +
                                  speedInfo.userInfo.rs.h >
                                0
                              ? false
                              : true
                        }
                        variant='contained'
                        endIcon={<Refresh />}
                        onClick={handleCalculate}
                      >
                        CALCULATE
                      </Button>
                    </Box>
                    {speedInfo.troopTier.length === 0 || speedInfo.troopsBatch === 0
                      ? true
                      : speedInfo.userInfo.ns.d * 24 +
                            speedInfo.userInfo.ns.h +
                            speedInfo.userInfo.rs.d * 24 +
                            speedInfo.userInfo.rs.h >
                          0
                        ? false
                        : true && (
                            <Box sx={{ display: 'flex', justifyContent: 'center', marginTop: '10px' }}>
                              <Typography variant='caption' color='error' align='center'>
                                You need to specify how many speeds you have in Normal and Recruitment Speeds.
                              </Typography>
                            </Box>
                          )}
                  </>
                )}
                {speedInfo.totalTroopsGrl > 0 && (
                  <>
                    <Divider>{<Typography variant='caption'>5 - RESULTS</Typography>}</Divider>
                    <Divider>{<Typography variant='caption'>5.1 - RECRUITMENT</Typography>}</Divider>
                    <TableContainer>
                      <Table
                        aria-labelledby='tableTitleTotals'
                        size={'small'}
                        aria-label='enhanced table totals'
                        sx={{
                          [`& .${tableCellClasses.root}`]: {
                            borderBottom: 'none'
                          }
                        }}
                      >
                        <TableBody>
                          <TableRow hover>
                            <TableCell align='right'>{<Typography color='primary'>Total</Typography>}</TableCell>
                            <TableCell align='center'>{''}</TableCell>
                            <TableCell align='center'>{''}</TableCell>
                          </TableRow>

                          <TableRow hover>
                            <TableCell align='right'>
                              {
                                <Typography variant='body1' color='textPrimary'>
                                  {Math.round(speedInfo.totalTroops).toLocaleString()}
                                </Typography>
                              }
                            </TableCell>
                            <TableCell align='left'>
                              {
                                <Typography variant='body1' color='primary'>
                                  Troops from Speeds
                                </Typography>
                              }
                            </TableCell>

                            <TableCell align='center'>{''}</TableCell>
                          </TableRow>
                          {recruitmentTemplates.find(p => p.recruitmentSpeed === speedInfo.profile)?.gemQty &&
                            speedInfo.userInfo.gems > 0 && (
                              <TableRow hover>
                                <TableCell align='right'>
                                  {
                                    <Typography variant='body1' color='textPrimary'>
                                      {Math.round(speedInfo.totalTroopsGems ?? 0).toLocaleString()}
                                    </Typography>
                                  }
                                </TableCell>
                                <TableCell align='left'>
                                  {
                                    <Typography variant='body1' color='primary'>
                                      Troops from Gems
                                    </Typography>
                                  }
                                </TableCell>

                                <TableCell align='center'>{''}</TableCell>
                              </TableRow>
                            )}
                          <TableRow hover>
                            <TableCell align='right'>
                              {
                                <Typography variant='h6' component='h2' color='textPrimary'>
                                  {Math.round(speedInfo.totalTroopsGrl).toLocaleString()}
                                </Typography>
                              }
                            </TableCell>
                            <TableCell align='left'>
                              {
                                <Typography variant='body1' color='primary'>
                                  Total Troops(All)
                                </Typography>
                              }
                            </TableCell>

                            <TableCell align='center'>{''}</TableCell>
                          </TableRow>
                          <TableRow hover>
                            <TableCell align='right'>
                              {
                                <Typography variant='h6' component='h2' color='textPrimary'>
                                  {recruitmentConfig.default.troopsPoints.find(t => t.value === speedInfo.troopTier)
                                    .points *
                                    speedInfo.totalTroopsGrl >
                                  0
                                    ? Math.round(
                                        recruitmentConfig.default.troopsPoints.find(
                                          t => t.value === speedInfo.troopTier
                                        ).points * speedInfo.totalTroopsGrl
                                      ).toLocaleString()
                                    : 0}
                                </Typography>
                              }
                            </TableCell>
                            <TableCell align='left'>
                              {
                                <Typography variant='body1' color='primary'>
                                  Total Points (Void/Frenzy)
                                </Typography>
                              }
                            </TableCell>

                            <TableCell align='center'>{''}</TableCell>
                          </TableRow>
                          <TableRow hover>
                            <TableCell align='right'>
                              {
                                <Typography variant='h6' component='h2' color='textPrimary'>
                                  {speedInfo.totalTroopsGrl > 0
                                    ? Math.round(
                                        speedInfo.totalTroopsGrl *
                                          recruitmentConfig.default.troopsPoints.find(
                                            t => t.value === speedInfo.troopTier
                                          ).bp
                                      ).toLocaleString()
                                    : 0}
                                </Typography>
                              }
                            </TableCell>
                            <TableCell align='left'>
                              {
                                <Typography variant='body1' color='primary'>
                                  Total Battle Power (BP)
                                </Typography>
                              }
                            </TableCell>

                            <TableCell align='center'>{''}</TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                    </TableContainer>
                    <Divider>{<Typography variant='caption'>5.2 - RSS NEEDED</Typography>}</Divider>
                    <Typography variant='body1' color='primary'>
                      {speedInfo.rss?.length > 0 &&
                        speedInfo.rss?.map(troop => (
                          <List key={troop.key}>
                            <ListItem alignItems='flex-start'>
                              <ListItemAvatar>
                                <Avatar alt={troop.key} src={`/images/colossus/${troop.name.toLowerCase()}.webp`} />
                              </ListItemAvatar>
                              <ListItemText
                                primary={`${troop.name} T${speedInfo.troopTier}`}
                                secondary={
                                  <>
                                    <Icon icon='game-icons:wheat' color='#e7ba83' />
                                    {`${formatCompactNumber(troop?.f)}  `}
                                    <Icon icon='game-icons:wood-pile' color='#c18439' />
                                    {`${formatCompactNumber(troop?.w)}  `}
                                    <Icon icon='game-icons:stone-pile' color='#ffffff' />
                                    {`${formatCompactNumber(troop?.s)}  `}
                                    <Icon icon='game-icons:metal-bar' color='#8f8881' />
                                    {`${formatCompactNumber(troop?.i)}  `}
                                  </>
                                }
                              />
                            </ListItem>
                            <Divider variant='inset' component='li' />
                          </List>
                        ))}
                    </Typography>
                  </>
                )}
              </Paper>
            </Grid>
          </Grid>
        </Card>
      </>
        <Grid size={{ xs: 12 }} sx={{ pt: 6 }}>
          <TablaReferencia titulo='Recruitment time per batch, by speed profile and troop tier' columnas={COLUMNAS_SPEEDS} filas={FILAS_SPEEDS} idFila='id' />
        </Grid>
    </>
  )
}

Speeds.noLoadingGate = true

export default Speeds
